import { isArray, isUndefined } from '@fxts/core';
import { $, Enable, html, rune, View } from 'rune-ts';
import { typoPcMo } from '../../../../shared/typography/typo';
import { DotIcon } from '../Icon';
import { Input } from './Input';
import klass from './Input.module.scss';
import { InputChangedEvent, InputFocusinEvent, InputFocusoutEvent } from './InputEvent';
import { InputHelper, InputHelperEnable } from './InputHelper';
import { InputProps, InputSide } from './type';

export class InputBase<
  D extends InputProps = InputProps,
  SV extends View<D> = View<D>,
  SE extends Enable<D> = Enable<D>,
  SO = any,
> extends View<D> {
  inputView: Input;
  inputHelperView: InputHelper;
  inputHelperEnable: InputHelperEnable<D, this>;
  inputSideView: SV;
  inputSideEnable: SE;

  constructor(data: D, input_side: InputSide<D, SV, SE, SO>) {
    super(data);
    this.inputView = new Input(data);
    this.inputHelperView = new InputHelper(data);
    this.inputHelperEnable = new InputHelperEnable(this);
    this.inputSideView = new input_side.InputSide(data, input_side.InputSideOption);
    this.inputSideEnable = new input_side.InputSideEnable(this);
    this.data.is_mobile = !!(rune.getSharedData(this)?.is_mobile ?? this.data.is_mobile);
  }

  override template() {
    const { required, custom_klass, readonly, disabled, error_helper, text_helper, name, title, is_mobile } =
      this.data;

    const has_text_helper = !!text_helper && !error_helper;
    const has_error_helper = !isUndefined(error_helper) && !isArray(error_helper);
    const has_error_helpers = isArray(error_helper) && error_helper.length > 0;

    return html`
      <label
        name="${name}"
        data-error="${this.inputHelperView.getIsError()}"
        ${has_text_helper ? html`data-has_text_helper="${has_text_helper}"` : ''}
        ${has_error_helper ? html`data-has_error_helper="${has_error_helper}"` : ''}
        ${has_error_helpers ? html`data-has_error_helpers="${has_error_helpers}"` : ''}
        class="${klass.input_container} ${custom_klass || ''}"
      >
        ${title
          ? html`<div
              class="${klass.title} ${typoPcMo({ is_mobile: !!is_mobile, mo: '14_bold', pc: '16_bold' })}"
            >
              ${title}
              ${required
                ? html`<div class="${klass.require_dot}"><span class="${klass.dot}">${DotIcon}</span></div>`
                : ''}
            </div>`
          : ''}
        <div ${readonly || disabled ? 'disabled' : ''} class="wrapper ${klass.input_wrapper}">
          ${this.inputView} ${this.inputSideView}
          <span class="${klass.border}"></span>
        </div>
        ${this.inputHelperView}
      </label>
    `;
  }

  override redraw(): this {
    const { readonly, disabled, error_helper, text_helper } = this.data;
    const has_text_helper = !!text_helper && !error_helper;
    const has_error_helper = !isUndefined(error_helper) && !isArray(error_helper);
    const has_error_helpers = isArray(error_helper) && error_helper.length > 0;

    const el$ = this.element() as HTMLLabelElement;
    el$.dataset.error = this.inputHelperView.getIsError().toString();
    el$.dataset.has_text_helper = has_text_helper.toString();
    el$.dataset.has_error_helper = has_error_helper.toString();
    el$.dataset.has_error_helpers = has_error_helpers.toString();

    const wrapper$ = $(el$).find('div.wrapper');
    if (wrapper$) {
      if (readonly || disabled) wrapper$.setAttribute('disabled', true);
      else wrapper$.removeAttribute('disabled');
    }

    this.subViewsFromTemplate.forEach((view) => view.redraw());

    return this;
  }

  protected override onRender() {
    this.addEventListener('focusin', () => {
      this.dispatchEvent(InputFocusinEvent, {});
    });
    this.addEventListener('focusout', () => {
      this.dispatchEvent(InputFocusoutEvent, {});
    });
    this.delegate('change', Input, (e, input) => {
      this.changeHandler(e, input);
      this.dispatchEvent(InputChangedEvent, {});
    });
    this.delegate('keyup', Input, (e, input) => {
      this.changeHandler(e, input);
      this.dispatchEvent(InputChangedEvent, {});
    });
  }

  changeHandler(_: Event, input: Input) {
    if (this.data.delete_pattern) {
      const origin_value = input.getValue();
      const result_value = origin_value.replace(this.data.delete_pattern, '');
      input.setValue(result_value);
      this.data.value = result_value;
    } else {
      this.data.value = input.getValue();
    }
  }

  blur() {
    this.inputView.element().blur();
  }

  focus() {
    this.inputView.element().focus();
  }

  isError() {
    return this.inputHelperView.is_error;
  }

  getSelector() {
    return '.' + this.toString() + '[name="' + this.data.name + '"]';
  }
}
